import { useState, useEffect } from 'react';
import { C, t } from '../constants/dashboardConstants';
import { getScheduleAccentBackground, normalizeScheduleColor } from './scheduleColors';
import LoadingSpinner from '../components/LoadingSpinner';

const WEEK_DAYS = ['일', '월', '화', '수', '목', '금', '토'];

function toDateStr(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function normalizeDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function getMonthGrid(year, month) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrevMonth = new Date(year, month, 0).getDate();
  const days = [];

  for (let i = firstDay - 1; i >= 0; i--) {
    days.push({ date: new Date(year, month - 1, daysInPrevMonth - i), type: 'prev' });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    days.push({ date: new Date(year, month, d), type: 'current' });
  }
  let nextCounter = 1;
  while (days.length % 7 !== 0 || days.length < 35) {
    days.push({ date: new Date(year, month + 1, nextCounter++), type: 'next' });
  }
  return days;
}

function getWeekDays(refDate) {
  const day = refDate.getDay();
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(refDate);
    d.setDate(refDate.getDate() - day + i);
    return d;
  });
}

function EventChip({ event, onClick }) {
  const color = normalizeScheduleColor(event.color || event.eventColor);
  const background = getScheduleAccentBackground(color);
  return (
    <div
      onClick={e => { e.stopPropagation(); onClick(event); }}
      style={{
        height: '26px', flexShrink: 0,
        background,
        border: `1px solid ${color}`,
        borderRadius: '5px', padding: '0 8px', cursor: 'pointer',
        display: 'flex', alignItems: 'center',
      }}
    >
      <span style={{
        fontFamily: 'Pretendard, sans-serif', fontSize: '14px', fontWeight: 500,
        letterSpacing: '-0.04em', lineHeight: 1, color,
        display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        width: '100%',
      }}>
        {event.title}
      </span>
    </div>
  );
}

function DayCell({ date, isOutsideMonth, events, onEventClick, onDayClick }) {
  return (
    <div
      onClick={() => onDayClick(date, events)}
      style={{
        flex: 1, minWidth: 0, height: 122,
        background: C.white, border: `1px solid ${C.g200}`,
        borderRadius: '8px', padding: '12px',
        boxSizing: 'border-box',
        display: 'flex', flexDirection: 'column', gap: '10px',
        cursor: 'pointer',
      }}
    >
      <span style={t('16px', 400, isOutsideMonth ? C.g300 : C.sub)}>{date.getDate()}</span>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {events.slice(0, 2).map(ev => (
          <EventChip key={ev.id} event={ev} onClick={onEventClick} />
        ))}
      </div>
    </div>
  );
}

export default function CalendarGrid({ events, isLoading, onEventClick, onDayClick, onMonthChange }) {
  const [mode] = useState('월');
  const [ref, setRef] = useState(new Date());

  const year = ref.getFullYear();
  const month = ref.getMonth();

  useEffect(() => {
    if (mode === '월' && onMonthChange) onMonthChange(year, month);
  }, [year, month, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const eventsForDate = (date) => {
    const target = normalizeDate(date);
    return events.filter((event) => {
      const start = normalizeDate(event.startAt || event.date);
      const end = normalizeDate(event.endAt || event.startAt || event.date);
      if (!start || !end || !target) return false;
      return target >= start && target <= end;
    });
  };

  const handlePrev = () => {
    const d = new Date(ref);
    if (mode === '월') d.setMonth(d.getMonth() - 1);
    else if (mode === '주') d.setDate(d.getDate() - 7);
    else d.setDate(d.getDate() - 1);
    setRef(d);
  };

  const handleNext = () => {
    const d = new Date(ref);
    if (mode === '월') d.setMonth(d.getMonth() + 1);
    else if (mode === '주') d.setDate(d.getDate() + 7);
    else d.setDate(d.getDate() + 1);
    setRef(d);
  };

  //const handleToday = () => setRef(new Date());

  let dateLabel = '';
  if (mode === '월') {
    dateLabel = `${year}년 ${month + 1}월`;
  } else if (mode === '주') {
    const weekDays = getWeekDays(ref);
    const s = weekDays[0], e = weekDays[6];
    dateLabel = `${s.getFullYear()}년 ${s.getMonth() + 1}월 ${s.getDate()}일 ~ ${e.getFullYear()}년 ${e.getMonth() + 1}월 ${e.getDate()}일`;
  } else {
    dateLabel = `${year}년 ${month + 1}월 ${ref.getDate()}일`;
  }

  const iconBtn = {
    width: '34px', height: '34px',
    background: C.white, border: `1px solid ${C.g200}`,
    borderRadius: '6px', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };

  /* ── Month view ── */
  const renderMonth = () => {
    const grid = getMonthGrid(year, month);
    const rows = [];
    for (let i = 0; i < grid.length; i += 7) rows.push(grid.slice(i, i + 7));
    return (
      <>
        <div style={{ display: 'flex', gap: '8px' }}>
          {WEEK_DAYS.map(d => (
            <div key={d} style={{ flex: 1, display: 'flex', justifyContent: 'center', padding: '10px 0' }}>
              <span style={t('20px', 600, C.bk)}>{d}</span>
            </div>
          ))}
        </div>
        {rows.map((row, ri) => (
          <div key={ri} style={{ display: 'flex', gap: '8px' }}>
            {row.map((cell, ci) => (
              <DayCell
                key={ci}
                date={cell.date}
                isOutsideMonth={cell.type !== 'current'}
                events={eventsForDate(cell.date)}
                onEventClick={onEventClick}
                onDayClick={onDayClick}
              />
            ))}
          </div>
        ))}
      </>
    );
  };

  /* ── Week view ── */
  const renderWeek = () => {
    const weekDays = getWeekDays(ref);
    return (
      <>
        <div style={{ display: 'flex', gap: '8px' }}>
          {WEEK_DAYS.map(d => (
            <div key={d} style={{ flex: 1, display: 'flex', justifyContent: 'center', padding: '10px 0' }}>
              <span style={t('20px', 600, C.bk)}>{d}</span>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {weekDays.map((date, i) => (
            <DayCell
              key={i}
              date={date}
              isOutsideMonth={false}
              events={eventsForDate(date)}
              onEventClick={onEventClick}
              onDayClick={onDayClick}
            />
          ))}
        </div>
      </>
    );
  };

  /* ── Day view ── */
  const renderDay = () => {
    const dayEvents = eventsForDate(ref);
    return (
      <div style={{
        background: C.white, border: `1px solid ${C.g200}`,
        borderRadius: '8px', padding: '30px',
        minHeight: '300px', boxSizing: 'border-box',
      }}>
        {dayEvents.length === 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px' }}>
            <span style={t('16px', 400, C.sub)}>예정된 일정이 없습니다.</span>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {dayEvents.map(ev => (
              <div
                key={ev.id}
                onClick={() => onEventClick(ev)}
                style={{
                  background: getScheduleAccentBackground(ev.color || ev.eventColor),
                  border: `1px solid ${normalizeScheduleColor(ev.color || ev.eventColor)}`,
                  borderRadius: '8px', padding: '16px 20px',
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: '12px',
                }}
              >
                <span style={t('16px', 500, normalizeScheduleColor(ev.color || ev.eventColor))}>{ev.title}</span>
                <span style={t('14px', 400, C.g500)}>{ev.time}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="4" width="18" height="18" rx="2" stroke={C.bk} strokeWidth="1.5"/>
            <path d="M3 9h18M8 2v4M16 2v4" stroke={C.bk} strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          <span style={t('20px', 600, C.bk)}>{dateLabel}</span>
          {isLoading && <LoadingSpinner size="sm" style={{ display: 'inline-block' }} />}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button onClick={handlePrev} style={iconBtn}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M10 12L6 8l4-4" stroke={C.bk} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <button onClick={handleNext} style={iconBtn}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M6 12l4-4-4-4" stroke={C.bk} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Calendar body */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {mode === '월' && renderMonth()}
        {mode === '주' && renderWeek()}
        {mode === '일' && renderDay()}
      </div>
    </div>
  );
}
